import React, { useEffect, useRef } from 'react'
import { Box, Dialog, DialogActions, DialogContent, Button } from '@mui/material'
import type { ButtonProps } from '@mui/material/Button'
import type { SxProps, Theme } from '@mui/material/styles'
import { kiotButtonBaseSx, kiotButtonCancelSx, kiotButtonPrimarySx } from '@/components/common/button/kiotButtonStyles'
import { FormKiotThemeProvider } from '@/components/common/formFields/FormKiotThemeProvider'
import {
  FORM_MODAL_VIEWPORT_MAX_HEIGHT,
  formModalBelowHeaderOverlaySx,
} from '@/components/common/styles/formModalLayout'
import { baseModalDefaultFooterSx } from './BaseModal'
import type { BaseModalFooterAction } from './BaseModal'

const resolveKiotFooterButtonSx = (variant: ButtonProps['variant'] = 'outlined') => {
  if (variant === 'contained') return kiotButtonPrimarySx
  if (variant === 'outlined') return kiotButtonCancelSx
  return kiotButtonBaseSx
}

/** Viền / bóng quanh modal phiếu bán hàng — gọn, theo theme, không viền dày. */
export const banHangPosBaseModalPaperBorderSx: SxProps<Theme> = {
  border: '1px solid var(--banhang-primary-surface-border, rgba(0, 0, 0, 0.12))',
  boxShadow: '0 12px 40px rgba(15, 23, 42, 0.14)',
  outline: 'none',
}

export type BanHangPosBaseModalProps = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  width?: string | number
  contentSx?: SxProps<Theme>
  paperSx?: SxProps<Theme>
  footerActions?: BaseModalFooterAction[]
  footerSx?: SxProps<Theme>
  /**
   * `true` → canh giữa. Mặc định neo sát dưới header menu (form POS dài).
   */
  centered?: boolean
}

/**
 * Base modal nghiệp vụ bán hàng (đặt hàng / hóa đơn / trả hàng…).
 * Không có DialogTitle riêng — header (close + tìm hàng + title) nằm trên chrome POS.
 */
const BanHangPosBaseModal: React.FC<BanHangPosBaseModalProps> = ({
  open,
  onClose,
  children,
  width,
  contentSx,
  paperSx,
  footerActions,
  footerSx,
  centered = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.focus()
    }
  }, [open])

  const footerEls =
    footerActions && footerActions.length > 0 ? (
      <DialogActions
        sx={
          [
            baseModalDefaultFooterSx,
            ...(footerSx ? (Array.isArray(footerSx) ? footerSx : [footerSx]) : []),
          ] as SxProps<Theme>
        }
      >
        {footerActions.map((action, index) => {
          const key = action.id ?? index
          return (
            <Button
              key={key}
              type={action.type ?? 'button'}
              variant={action.variant ?? 'outlined'}
              color={action.color ?? 'primary'}
              size="medium"
              startIcon={action.startIcon}
              endIcon={action.endIcon}
              fullWidth={action.fullWidth}
              disabled={Boolean(action.disabled)}
              onClick={action.onClick}
              sx={[
                resolveKiotFooterButtonSx(action.variant),
                ...(action.sx ? (Array.isArray(action.sx) ? action.sx : [action.sx]) : []),
              ]}
            >
              {action.label}
            </Button>
          )
        })}
      </DialogActions>
    ) : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      sx={centered ? undefined : formModalBelowHeaderOverlaySx}
      slotProps={{
        backdrop: {
          sx: centered ? undefined : formModalBelowHeaderOverlaySx,
        },
        container: {
          sx: {
            alignItems: centered ? 'center' : 'flex-start',
          },
        },
        paper: {
          sx: [
            {
              ...(width != null && width !== '' ? { width } : {}),
              minHeight: 120,
              height: 'auto',
              maxHeight: FORM_MODAL_VIEWPORT_MAX_HEIGHT,
              mt: 0,
              mb: 0,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
            ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
            { borderRadius: '12px' },
            banHangPosBaseModalPaperBorderSx,
          ],
        },
      }}
    >
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        sx={
          [
            {
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              p: '0 !important',
            },
            ...(contentSx ? (Array.isArray(contentSx) ? contentSx : [contentSx]) : []),
          ] as SxProps<Theme>
        }
      >
        <FormKiotThemeProvider>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
        </FormKiotThemeProvider>
      </DialogContent>
      {footerEls}
    </Dialog>
  )
}

export default BanHangPosBaseModal
